import _ from "underscore";
import DriveView from "../../DriveView";
import { formInspectedVehicleString, getDefectNames, getLabel, getDefectListForDefect, translateDefectListName, getDeviceLicense, isFromAustralia } from "./helper";
import { populateDefectList, getDefectListsForAsset } from "../../utils/defectList";
import InspectionChecklist from "./inspectionList";
import DVIRLog, { TRepairStatus, IDvirDefects, REPAIR_STATUS_STRINGS, IDefectRemark } from "../../model/DVIRLog";
import { Device } from "../../model/Device";
import { TrailerAttachments } from "../../model/TrailerAttachments";
import Sync from "../../sync/sync";
import { populateAddressesRedux } from "../../sync/common";
import Api from "../../services/api";
import { dateTimeComparator, dstCalculator } from "../../utils/date";
import { getCurrentVehicle } from "../../utils/device";
import { BaseCache, makeDeviceCache, makeEffectiveTrailerDeviceCache, makeUserCache } from "../../utils/storage/cache";
import dayjs from "../../utils/dayjs";
import { handleApi } from "../../utils/error";
import { geocodeAddresses } from "../../utils/location";
import { htmlEscape, hashToArray, extend } from "../../utils/vanilla";
import { Dialog } from "../framework/dialog";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { getCachedImageForRemark } from "./imageHelper";
import { FeatureFlagName } from "../../utils/featureFlagName";
import { RoadsideCheckLanguageSelector } from "../inspection/roadsideCheckLanguageSelector";

import pageHTML from "./view.tmpl";
import defectHTML from "./defect-log.tmpl";
import logAddressHTML from "../framework/addressDialog.tmpl";
import { IDefect } from "../../model/Defect";
import { isNetworkError } from "../../utils/errorHandler/error.types";
import { getDisplayTimeSpan, secondsToHours } from "../../utils/helpers";

export default class ViewDvirView extends DriveView<any> {

    model: DVIRLog;

    protected strings = [
        "Expand previous inspection",
        "Collapse previous inspection",
        "Unknown",
        "Other",
        "This report identifies major defects with this {0}.",
        "vehicle",
        "trailer",
        "d",
        "h",
        "{minutes}m",
        "s",
        "Not available"
    ];
    // public as it will also be used by repair.ts
    private readonly template = _.template(pageHTML);
    private readonly defectLogTemplate = _.template(defectHTML);
    private readonly logAddressInputTemplate = _.template(logAddressHTML);
    private readonly sync: Sync;
    private readonly api: Api;
    private readonly currentUser: DeepReadonly<IUser> | null = this.app.userService.getCurrentUser();
    private readonly trailerAttachmentCollection: TrailerAttachments;
    private readonly users: IIdDictionary<IUser>;
    private readonly isInspectionMode: boolean;
    private readonly isTrailerDeviceEnabled: boolean;
    private isAustralia: boolean;
    private needLoadInTripDefects: boolean;
    private currentDefectList: IDefect;
    private language?: string;
    private openExpanded = false;
    private readonly languageSelector = new RoadsideCheckLanguageSelector({
        app: this.app,
        getTranslator: () => this.translator,
        getElement: () => this.el,
        getHosRuleSetIdShortened: () => this.app.hosUtils.getLatestHosRuleSetIdShortened(),
        onLanguageApplied: language => this.switchLanguage(language)
    });
    private mediaFileLoadFunctions: (() => void)[];
    private readonly trailerDeviceCache: BaseCache<Device | IDevice>;
    private readonly deviceCache: BaseCache<Device | IDevice>;
    private readonly userCache: BaseCache<IUser>;

    constructor(options) {
        super(options);
        this.sync = this.app.sync;
        this.api = this.app.api;
        if (!options.state) {
            return;
        }

        let id = options.state.id;
        if (id) {
            this.model = new DVIRLog({ id: id });
        } else {
            return;
        }

        this.isInspectionMode = options.inspection || false;
        this.needLoadInTripDefects = options.isOntario || false;
        this.language = this.isInspectionMode ? this.app.store.getState().mobile.language : undefined;
        this.trailerAttachmentCollection = new TrailerAttachments(this.api, this.app.store);
        this.mediaFileLoadFunctions = [];
        this.deviceCache = makeDeviceCache(this.app.store);
        this.trailerDeviceCache = makeEffectiveTrailerDeviceCache(this.app.store);
        this.userCache = makeUserCache(this.app.store);
        this.users = this.userCache.getDict();
        this.isTrailerDeviceEnabled = this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_CACHE_TRAILER_AS_DEVICE) &&
            this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.ALWAYS_POPULATE_DEVICE_ON_LOG);
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #expand-log": this.togglePreviousLog,
            "click #editAddress": this.addressInput,
            "click #view-defects": this.viewDefects,
            "click #roadside-check-language-selector": this.onLanguageSelectorClick
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    remove() {
        this.languageSelector.remove();
        return super.remove();
    }

    switchLanguage(language: string): void {
        if (language !== this.language) {
            this.language = language;
            this.renderImpl();
        }
    }

    onLanguageSelectorClick() {
        this.languageSelector.handleClick();
    }

    render() {
        this.populate();
        this.renderImpl();
        return this;
    }
    renderImpl() {
        // Unlikely, but in the off chance someone visits this page without going through review/inspect
        if (!(this.model && this.model.isActive())) {
            this.app.router.navigate("dvir/main", true);
            return undefined;
        }

        let model = this.model;
        const canEditDvir = this.app.securityService.can("useDvir");
        let needsInspect = canEditDvir && this.app.securityService.can("inspectDvir") && model.isComplete(this.app) && !model.hasBlockingUnrepairedDefect(this.app.store);
        let canCertifyDvir = canEditDvir && this.app.securityService.can("certifyDvir");

        let showAllDefects = this.isInspectionMode || !canEditDvir || model.isCertified();
        let defectsToShow = showAllDefects ? model.getClonedAllDefects() : model.getClonedRepairedDefects();

        if (this.needLoadInTripDefects) {
            let defectsDict: IIdDictionary<IDvirDefects> = {};
            let defectDate = model.dateTime;
            defectsToShow.forEach(defect => {
                if (defect.id) {
                    defect.dateTime = defectDate;
                    defectsDict[defect.id] = defect;
                }
            });

            let inTripDvirs = this.app.dvirLogsService.getInTripDvirsForAsset(
                this.app.dvirLogsService.getDvirLogs(),
                this.model.getResource().id,
                model.dateTime
            );
            inTripDvirs.forEach(dvir => {
                let inTripDate = dvir.dateTime;
                dvir.dVIRDefects?.forEach(defect => {
                    if (defect.id) {
                        if (defectsDict.hasOwnProperty(defect.id)) {
                            defectsDict[defect.id] = extend({}, defect, defectsDict[defect.id]);
                        } else {
                            defect.dateTime = inTripDate;
                            defectsDict[defect.id] = defect;
                        }
                    }
                });
            });

            defectsToShow = hashToArray(defectsDict);
            this.model.dVIRDefects = defectsToShow;
            this.needLoadInTripDefects = false;
        }

        let needsRepair = canEditDvir && model.hasUnrepairedDefects() && !model.isCertified() && (!canCertifyDvir || !model.needsReview(this.app.store));
        let allowCollapsed = !this.isInspectionMode && (needsInspect || needsRepair);

        // The selector is only offered while viewing the DVIR as part of a Roadside Check.
        const languageViewModel = this.languageSelector.getViewModel(this.isInspectionMode);

        return Promise.all([
            getDefectNames(this.translator, this.api, this.app.store, defectsToShow, model.getAssetType(), this.language),
            this.getDefectList(),
            this.app.localizerService.initUserUnits(this.translator, this.language),
            this.translate(this.language)
        ])
            .then(([populatedDefectsDict]) => {
                let populatedParts = Object.keys(populatedDefectsDict);
                let populatedDefectsCount = populatedParts.length > 0 ? populatedParts.reduce((sum, defectId) => sum + populatedDefectsDict[defectId].length, 0) : 0;
                let failedDefectsLoad = defectsToShow.length !== populatedDefectsCount;
                let isDevice = model.getAssetType() === "Device";
                let templateObj = {
                    ...this.getCompanyInformation(),
                    log: model,
                    isDevice,
                    criticalInspectionString: this.getString("This report identifies major defects with this {0}.").replace("{0}", isDevice ? this.getString("vehicle") : this.getString("trailer")),
                    inspectionDate: model.dateTime ? this.formatDate(dstCalculator.toLocalDateTime(model.dateTime)) : null,
                    certifyDate: model.certifyDate ? this.formatDate(dstCalculator.toLocalDateTime(model.certifyDate)) : null,
                    information: null as any,
                    populatedDefects: [] as any,
                    canEditDvir,
                    canCertifyDvir,
                    needsReview: model.needsReview(this.app.store),
                    hasBlockingUnrepairedDefect: model.hasBlockingUnrepairedDefect(this.app.store),
                    logType: this.getDvirLogTypeString(model),
                    isInspectionMode: this.isInspectionMode,
                    failedDefectsLoad,
                    showAllDefects: showAllDefects && this.model.getUnrepairedDefects().length,
                    vehicleIdentificationNumber: this.getVIN(),
                    authorityName: model.authorityName || this.currentUser && this.currentUser.authorityName || "",
                    authorityAddress: model.authorityAddress || this.currentUser && this.currentUser.authorityAddress || "",
                    inspectorName: this.getDriverName(model.driver),
                    operatorName: this.getOperatorName(model),
                    deviceName: isDevice && model.device.name,
                    assetName: model.getResource().name,
                    allowCollapsed,
                    needsRepair,
                    certifiedByName: this.getDriverName(model.certifiedBy),
                    ...languageViewModel
                };

                templateObj.information = this.getInformation(model);
                templateObj.populatedDefects = this.getDefectsHtml(populatedDefectsDict);
                return this.display(this.template(templateObj), this.language);
            })
            .then(() => {
                this.openExpanded && allowCollapsed && this.togglePreviousLog();
                return this.populateAddress();
            })
            .then(() => this.getInspectionString(model))
            .then(() => this.loadAllMediaFiles());
    }

    getDriverName(driver: IUser | "NoUserId"): string {
        if (!driver || typeof driver === "string") {
            return "";
        }

        return !driver.firstName && !driver.lastName
            ? this.getString("Not available")
            : `${ driver.firstName ?? "" } ${ driver.lastName ?? "" }`.trim();
    }

    getOperatorName(log: DVIRLog) {
        let users = this.users;
        let driver = log.driver;
        if (driver !== "NoUserId") {
            if (users.hasOwnProperty(driver.id)) {
                log.driver = users[driver.id];
            } else if (this.currentUser && driver.id === this.currentUser.id) {
                log.driver = extend(true, {}, this.currentUser) as IUser;
            }
        }
        return this.getDriverName(log.driver);
    }

    getVIN() {
        let model = this.model;
        let currentVehicle = getCurrentVehicle();
        let id = model.getResource().id;

        if (currentVehicle && currentVehicle.id === id) {
            let device = model.device = new Device(this.deviceCache.get({ id })[0]);
            return device.vehicleIdentificationNumber;
        }

        return null;
    }

    closeExpanded() {
        this.openExpanded = false;
    }

    loadAllMediaFiles() {
        for (let i = 0; i < this.mediaFileLoadFunctions.length; i++) {
            this.mediaFileLoadFunctions[i]();
        }
    }

    getCompanyInformation() {
        if (this.model.driver === "NoUserId") {
            return { homeTerminal: "", homeTerminalAddress: "" };
        }
        return {
            homeTerminal: this.model.driver.companyName || "",
            homeTerminalAddress: this.model.driver.companyAddress || ""
        };
    }

    private getDvirLogTypeString(dvirLog: DVIRLog): string {
        switch (dvirLog.logType) {
            case "PreTrip":
                return "Pre-trip";
            case "InTrip":
                return "In-trip";
            case "PostTrip":
                return "Post-trip";
            default:
                return "Unknown";
        }
    }

    private populateAddress(): Promise<void> {
        let location = this.model.getLocation();
        let existingAddress = this.model._address;
        let addressEl = document.getElementById("dvir_address");
        let showUnknown = (addressElement: HTMLElement | null) => {
            if (addressElement) {
                addressElement.textContent = this.getString("Unknown");
            }
        };
        if (existingAddress && addressEl) {
            addressEl.textContent = existingAddress;
        } else if (location && location.location) {
            return populateAddressesRedux(this.api, this.app.store, [{ id: this.model.id, coordinate: location.location }], true)
                .then(([address]) => {
                    let formattedAddress = address && address.formattedAddress;
                    let region: string | undefined = address && address.region || undefined;
                    let country: string | undefined = address && address.country || undefined;
                    this.model._address = formattedAddress;
                    this.model._region = region;
                    this.model._country = country;
                    if (addressEl && formattedAddress) {
                        addressEl.textContent = formattedAddress;
                    }
                });
        } else {
            showUnknown(addressEl);
        }
        return Promise.resolve();
    }

    private populate() {
        this.app.dvirLogsService.resetProperties(this.model);
        this.isAustralia = isFromAustralia(getDeviceLicense(this.app.store, this.model._device));

        let model = this.model;
        let users = this.users;
        let id: string = model.getResource().id;
        let currentVehicle = getCurrentVehicle();
        if (model.getResourceType() === "trailer" && id !== "NoTrailerId") {
            let trailer = this.trailerAttachmentCollection.findTrailer(id);
            if (trailer) {
                model.trailer = trailer;
            }
            if (model.device.id && this.isTrailerDeviceEnabled) {
                model.device = new Device(this.trailerDeviceCache.get({ id: model.device.id })[0]);
            }
        } else if (currentVehicle && currentVehicle.id === id) {
            model.device = new Device(this.deviceCache.get({ id })[0]);
        }

        let driver = model.driver;
        if (driver !== "NoUserId") {
            if (users.hasOwnProperty(driver.id)) {
                model.driver = users[driver.id];
            } else if (this.currentUser && driver.id === this.currentUser.id) {
                model.driver = extend(true, {}, this.currentUser) as IUser;
            }
        }

        if (model.isCertified()) {
            let certifiedUser = model.certifiedBy;

            if (certifiedUser !== "NoUserId" && this.users.hasOwnProperty(certifiedUser.id)) {
                model.certifiedBy = this.users[certifiedUser.id];
            }
        }
    }

    private getDefectsHtml(defectsDict: { [id: string]: IDvirDefects[] }) {
        let defectsHtmlArray: string[] = [];

        // MYG-113773: Must reset these async functions to avoid duplications
        this.mediaFileLoadFunctions = [];

        Object.keys(defectsDict).forEach(defectParentId => {
            let defects = defectsDict[defectParentId];
            defects.forEach(dvirDefect => {
                let repairDate = dvirDefect.repairDateTime;
                if (repairDate) {
                    dvirDefect._repairDateTime = this.formatDate(repairDate);
                }

                if (dvirDefect.dateTime) {
                    dvirDefect._dateTime = this.formatDate(dvirDefect.dateTime);
                }

                let repairedUser = dvirDefect.repairUser;
                let repairUserName = "";
                if (repairedUser && this.users.hasOwnProperty(repairedUser.id)) {
                    dvirDefect.repairUser = this.users[repairedUser.id];
                    repairUserName = this.getDriverName(this.users[repairedUser.id]);
                }

                let repairStatus = REPAIR_STATUS_STRINGS[dvirDefect.repairStatus as TRepairStatus];

                if (dvirDefect.defectRemarks && dvirDefect.defectRemarks.length > 0) {
                    dvirDefect.defectRemarks.forEach(remark => {
                        remark.remark = htmlEscape(remark.remark);
                    });
                    dvirDefect.defectRemarks.sort(dateTimeComparator());
                }

                let severity = (dvirDefect.defect as IDefect).severity;
                let filteredRemarks = dvirDefect.defectRemarks;
                let remarksWithMediaFiles: IDefectRemark[] = [];
                if (filteredRemarks) {
                    if (this.isInspectionMode) {
                        filteredRemarks = filteredRemarks.filter(remark => remark.remark !== "See Image");
                    } else {
                        for (let i = 0; i < filteredRemarks.length; i++) {
                            let remark = filteredRemarks[i];
                            if (remark.mediaFiles && remark.mediaFiles.length > 0) {
                                let remarkWithMediaId = remark.id;
                                let mediaFileId = remark.mediaFiles[0].id;
                                if (mediaFileId && remarkWithMediaId) {
                                    const capturedRemarkId = remarkWithMediaId;
                                    const capturedMediaFileId = mediaFileId;
                                    this.mediaFileLoadFunctions.push(() => {
                                        void getCachedImageForRemark(capturedRemarkId, capturedMediaFileId, this.app.store, this.translator);
                                    });
                                }
                                remarksWithMediaFiles.push(remark);
                            }
                        }
                        filteredRemarks = filteredRemarks.filter(remark => !remark.mediaFiles || (remark.mediaFiles && remark.mediaFiles.length === 0));
                    }
                }

                let defectName = (dvirDefect.defect as IDefect).name;
                let partName = defectName;
                if (!(dvirDefect.defect as IDefect).parent) {
                    if (!defectName) {
                        // MYG-135056: only fall back to "Other" when the defect has neither
                        // a parent nor a name. A parentless defect with a real name must keep it.
                        defectName = this.getString("Other");
                    }
                } else {
                    partName = (dvirDefect.defect as IDefect).parent?.name || "";
                }

                defectsHtmlArray.push(this.defectLogTemplate({
                    dvirDefect,
                    defectName,
                    partName,
                    repairUserName: repairUserName ? `${ repairUserName }, ` : "",
                    hasRemarks: (filteredRemarks && filteredRemarks.length > 0) || (remarksWithMediaFiles && remarksWithMediaFiles.length > 0),
                    repairStatus,
                    criticalLabel: getLabel(severity),
                    severity: severity || "Normal",
                    filteredRemarks,
                    remarksWithMediaFiles: remarksWithMediaFiles
                }));
            });
        });
        return defectsHtmlArray;
    }

    private getInformation(model: DVIRLog) {
        let odometer = model.odometer || model._odometer;
        let engineHours = model.engineHours ?
            `${ secondsToHours(model.engineHours).toFixed(1) } ${ this.getString("h") }` : undefined;
        let duration = this.model.duration
                        ? getDisplayTimeSpan(this.model.duration, true, this.getString("d"), this.getString("h"), this.getString("{minutes}m"), this.getString("s"))
                        : undefined;
        let loadHeight = model.loadHeight;
        let loadWidth = model.loadWidth;
        if (model.getAssetType() === "Device") {
            let currentDevice = model.device;
            let getAttachedTrailers = (dateTime: Date, deviceId: string) => {
                let dvirTrailers = this.trailerAttachmentCollection.searchForTrailers(dateTime, dateTime, deviceId);
                return dvirTrailers.map(trailer => trailer.name);
            };
            return {
                odometer: odometer && this.app.localizerService.toLocalDistanceString(odometer),
                engineHours,
                plate: currentDevice.licensePlate && currentDevice.licensePlate,
                jurisdiction: currentDevice.licenseState && currentDevice.licenseState,
                trailers: model.dateTime ? getAttachedTrailers(new Date(model.dateTime), currentDevice.id) : null,
                loadHeight: loadHeight && this.app.localizerService.toLocalDistanceString(loadHeight, false),
                loadWidth: loadWidth && this.app.localizerService.toLocalDistanceString(loadWidth, false),
                duration,
                isInspectionMode: this.isInspectionMode
            };
        } else {
            return {
                odometer: odometer && this.app.localizerService.toLocalDistanceString(odometer),
                loadHeight: loadHeight && this.app.localizerService.toLocalDistanceString(loadHeight, false),
                loadWidth: loadWidth && this.app.localizerService.toLocalDistanceString(loadWidth, false),
                plate: this.isTrailerDeviceEnabled && model.device?.licensePlate ? model.device?.licensePlate : undefined,
                jurisdiction: this.isTrailerDeviceEnabled && model.device?.licenseState ? model.device?.licenseState : undefined,
                duration,
                isInspectionMode: this.isInspectionMode
            };
        }

    }

    private togglePreviousLog() {
        let dvirView = document.querySelector("#dvir-view");
        let collapseClass = "collapsed";
        let setButtonText = (texts: string) => {
            let expandButton = <HTMLElement>document.querySelector("#expand-log");
            if (expandButton) {
                expandButton.textContent = ` ${ texts }`;
            }
        };

        if (dvirView) {
            if (dvirView.classList.contains(collapseClass)) {
                dvirView.classList.remove(collapseClass);
                setButtonText(this.getString("Collapse previous inspection"));
            } else {
                dvirView.classList.add(collapseClass);
                setButtonText(this.getString("Expand previous inspection"));
            }
        }
    }

    private addressInput() {
        let showAddressErrorDialog = () => {
            let addressErrorDialog = new Dialog(
                "address-error",
                "The address you entered could not be found. Please re-enter it and try again.",
                this.translator,
                "Unknown address",
                undefined,
                {
                    isNightMode: this.app.store.getState().data.nightMode
                }
            );
            void addressErrorDialog.open();
        };
        let addressDialog = new Dialog(
            "dvir-address-input",
            this.logAddressInputTemplate({ isAustralia: this.isAustralia }),
            this.translator,
            "Enter an address",
            [{
                id: "dvirAddressCancel",
                className: "button_secondary",
                text: "Cancel",
                callback: () => {
                    addressDialog.remove();
                }
            }, {
                id: "dvirAddressOK",
                text: "OK",
                spinner: true,
                callback: () => {
                    // Fetch new model for set, since other one has been modified for display
                    let model = new DVIRLog({ id: this.model.id });
                    this.app.dvirLogsService.resetProperties(model);

                    let thisDialog = document.getElementById("dvir-address-input");
                    if (!thisDialog) {
                        addressDialog.remove();
                        return;
                    }

                    let street = (<HTMLInputElement>thisDialog.querySelector("#tempaddress-street")).value.trim();
                    let city = (<HTMLInputElement>thisDialog.querySelector("#tempaddress-city")).value.trim();
                    let stateProvince = (<HTMLInputElement>thisDialog.querySelector("#tempaddress-state")).value.trim();
                    let addressCountry = this.isAustralia ? "Australia" : (<HTMLInputElement>thisDialog.querySelector("#tempaddress-country")).value.trim();

                    let formattedAddress = `${ (street ? `${ street }, ` : "") }${ (city ? `${ city }, ` : "") }`
                        + `${ (stateProvince ? `${ stateProvince }, ` : "") }${ (addressCountry || "") }`;

                    let handleSuccess = () => {
                        void this.sync.dvirs.setItem(model.toJSON()).then(() => {
                            this.openExpanded = true;
                            dispatcher.trigger(DispatcherEvents.DvirUpdatedAddress);
                            addressDialog.remove();
                        });
                    };
                    let handleError = () => {
                        addressDialog.remove();
                        showAddressErrorDialog();
                    };

                    geocodeAddresses(this.api, [formattedAddress])
                        .then(addressArray => {
                            if (addressArray[0] !== null && !(addressArray[0].x === 0 && addressArray[0].y === 0)) {
                                model.location = {
                                    location: {
                                        x: addressArray[0].x,
                                        y: addressArray[0].y
                                    }
                                };

                                // Delete address so the correctly formatted one can be looked up later
                                // We might have set _isManualAddress, so remove that too
                                delete model._address;
                                delete model._region;
                                delete model._country;
                                delete model._isManualAddress;
                                handleSuccess();
                            } else {
                                // Address might not exist. Show error
                                handleError();
                            }
                        })
                        .catch(error => {
                            // Only condition we keep the address is if network error, because we can try later in the sync. Otherwise, show error
                            if (isNetworkError(error)) {
                                model._address = formattedAddress;
                                model._isManualAddress = true;
                                handleSuccess();
                            } else {
                                handleApi(error);
                                handleError();
                            }
                        });
                }
            }],
            {
                closable: false,
                isNightMode: this.app.store.getState().data.nightMode
            }
        );
        void addressDialog.open();
    }

    private getDefectList(): Promise<IDefect | null> {
        if (this.currentDefectList) {
            return Promise.resolve(this.currentDefectList);
        }

        return new Promise((resolve) => {
            let fetchedDefectList: IDefect | undefined;
            let getPopulateDefectListTask = () =>
                this.model.defectList ? populateDefectList(this.api, this.app.store, this.model.defectList) : Promise.resolve(null);

            return getPopulateDefectListTask()
                .then(defectList => {
                    if (!defectList) {
                        if (this.model.dVIRDefects && this.model.dVIRDefects.length > 0) {
                            return getDefectListForDefect(this.api, this.app.store, this.model.dVIRDefects[0].defect, this.model.getAssetType());
                        } else {
                            return getDefectListsForAsset(this.api, this.app.store, this.model.getResource() as IIdEntity, this.model.getAssetType())
                                .then(defectLists => defectLists && defectLists[0]);
                        }
                    }
                    return Promise.resolve(defectList);
                })
                .then(defectList => {
                    if (defectList) {
                        fetchedDefectList = defectList;
                        return translateDefectListName(defectList.name, this.translator, this.language);
                    }
                    return Promise.resolve("");
                })
                .then(defectListName => {
                    if (fetchedDefectList) {
                        fetchedDefectList.name = htmlEscape(defectListName);
                        this.currentDefectList = fetchedDefectList;
                    }
                    resolve();
                });
        });
    }

    // INSPECTION ONLY
    private viewDefects(e: Event) {
        let viewButton = e.currentTarget as HTMLInputElement;
        viewButton.disabled = true;

        return this.getDefectList()
            .then(() => {
                let inspectionChecklist = new InspectionChecklist({
                    translator: this.translator,
                    language: this.language,
                    model: this.model,
                    viewOnly: true,
                    defectList: this.currentDefectList,
                    store: this.app.store
                }, this.app.userService, this.app.userHosRuleSetService, this.app.securityService);

                return inspectionChecklist.renderList();
            })
            .then(defectListHtml => {
                let defectDialog = new Dialog(
                    "view-defect-list",
                    [defectListHtml],
                    this.translator,
                    `<span class="noTranslate">${ this.currentDefectList.name }</span>`,
                    undefined,
                    {
                        isMaxHeight: true,
                        minWidth: 300
                    }
                );
                return defectDialog.open(this.translator, this.language);
            })
            .then(() => viewButton.disabled = false)
            .catch(handleApi);
    }

    private getInspectionString(model: DVIRLog) {
        return formInspectedVehicleString(this.translator, model, this.currentDefectList, this.app.store, this.language)
            .then(inspectedVehicleString => {
                let dvirInspectCaption = document.getElementById("dvir-inspection-caption");
                if (dvirInspectCaption) {
                    dvirInspectCaption.textContent = inspectedVehicleString;
                }
            });
    }

    private formatDate(date: Date | string) {
        if (this.isInspectionMode && this.language) {
            return dayjs(dstCalculator.toLocalDateTime(date)).locale(this.language).format("LLL");
        }
        return dayjs(dstCalculator.toLocalDateTime(date)).format("LLL");
    }
}